import type { ReactNode } from 'react'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { formatUserFacingError } from '@/lib/errors'

interface AdminListPageProps {
  title: string
  action?: ReactNode
  filters?: ReactNode
  error?: string | null
  loading: boolean
  skeletonRows?: number
  children: ReactNode
  dialog?: ReactNode
  /** Whether more pages are available. When true a Load More button is shown. */
  hasMore?: boolean
  /** Whether a subsequent page is currently being fetched. */
  loadingMore?: boolean
  /** Called when the user clicks Load More. */
  onLoadMore?: () => void
  /** When true, skips the default rounded-border container around children — for card grids or other custom layouts. */
  bare?: boolean
}

export function AdminListPage({
  title,
  action,
  filters,
  error,
  loading,
  skeletonRows = 5,
  children,
  dialog,
  hasMore = false,
  loadingMore = false,
  onLoadMore,
  bare = false,
}: AdminListPageProps) {
  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="text-3xl font-bold">{title}</h1>
        {action}
      </div>

      {filters}

      {error && (
        <p className="text-sm text-destructive">
          {formatUserFacingError(
            error,
            'We could not load this list. Please try again.',
            'admin/list-load',
          )}
        </p>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: skeletonRows }).map((_, i) => (
            <Skeleton key={i} className="h-12 w-full" />
          ))}
        </div>
      ) : bare ? (
        <>{children}</>
      ) : (
        <div className="rounded-md border">{children}</div>
      )}

      {!loading && (hasMore || loadingMore) && (
        <div className="flex flex-col items-center gap-2">
          {loadingMore ? (
            <div className="w-full space-y-2">
              {Array.from({ length: 3 }).map((_, i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <Button variant="outline" onClick={onLoadMore}>
              Load more
            </Button>
          )}
        </div>
      )}

      {dialog}
    </div>
  )
}
